import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "pages/api/api";
import { useAuth } from "pages/context/AuthContext";
import Draggable from "react-draggable";

import * as XLSX from "xlsx-js-style";

import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tooltip,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLazyAccordion from "pages/components/DashboardLazyAccordion";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableViewIcon from "@mui/icons-material/TableView";

function metricLabel(k) {
  if (k === "qtd") return "qtd";
  if (k === "euros") return "euros";
  if (k === "litros") return "litros";
  if (k === "clientes") return "clientes";
  return k;
}

function toISO(d) {
  if (!d) return "";

  const value = String(d).trim();
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);

  return match ? match[1] : value;
}

function safeNum(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function filtrarClientesTodasSubmarcas(linhas, submarcasSelecionadas) {
  const arr = Array.isArray(linhas) ? linhas : [];
  const submarcas = Array.isArray(submarcasSelecionadas) ? submarcasSelecionadas : [];

  const refsSubmarcas = new Set(submarcas.map((x) => String(x?.ref || "").trim()).filter(Boolean));

  if (refsSubmarcas.size === 0) {
    return arr;
  }

  // Para TODAS_SUBMARCAS, um cliente só cumpre quando tem
  // saldo positivo de produto efetivo (FERT) em TODAS as
  // submarcas selecionadas.
  //
  // TARA e outros tipos não participam nesta validação.
  const qtdPorClienteSubmarca = new Map();

  arr.forEach((x) => {
    const refCliente = String(x?.refcliente || "").trim();
    const refSubmarca = String(x?.refsubmarca || "").trim();
    const refMaterialTipo = String(x?.refmaterialtipo || "")
      .trim()
      .toUpperCase();

    if (!refCliente) return;
    if (!refsSubmarcas.has(refSubmarca)) return;

    // Apenas produto efetivo determina o cumprimento.
    if (refMaterialTipo !== "FERT") return;

    if (!qtdPorClienteSubmarca.has(refCliente)) {
      qtdPorClienteSubmarca.set(refCliente, new Map());
    }

    const qtdPorSubmarca = qtdPorClienteSubmarca.get(refCliente);

    qtdPorSubmarca.set(refSubmarca, safeNum(qtdPorSubmarca.get(refSubmarca)) + safeNum(x?.qtd));
  });

  const clientesQueCumprem = new Set(
    [...qtdPorClienteSubmarca.entries()]
      .filter(([, qtdPorSubmarca]) =>
        [...refsSubmarcas].every((refSubmarca) => safeNum(qtdPorSubmarca.get(refSubmarca)) > 0)
      )
      .map(([refCliente]) => refCliente)
  );

  // Depois de validado o cliente pelas linhas FERT,
  // mantemos todas as linhas desse cliente no histórico:
  // FERT, TARA, devoluções, etc.
  return arr.filter((x) => clientesQueCumprem.has(String(x?.refcliente || "").trim()));
}

function VendedoresMiniTable({ vendedores = [], tipoKey = "qtd", onInfoVendedor }) {
  const rows = Array.isArray(vendedores) ? vendedores : [];

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fmt = (n) => {
    const v = safeNum(n);
    // mostra 0 sem casas; se quiseres sempre 2 casas, muda para toFixed(2)
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  };

  const calc = rows
    .map((v) => {
      const met = v?.[tipoKey] || {};
      const objetivo = safeNum(met.objetivo);
      const real = safeNum(met.vendas);
      if (objetivo <= 0) return null; // só mostra vendedores com objetivo definido
      const dif = real - objetivo;
      const perc = objetivo > 0 ? (real / objetivo) * 100 : null;

      return {
        key: v?.refvendedor || v?.vendedor || Math.random(),
        refvendedor: v?.refvendedor || "",
        vendedor: v?.vendedor || v?.refvendedor || "",
        objetivo,
        real,
        dif,
        perc,
      };
    })
    .filter(Boolean);

  const totalObj = calc.reduce((a, r) => a + safeNum(r.objetivo), 0);
  const totalReal = calc.reduce((a, r) => a + safeNum(r.real), 0);
  const totalDif = totalReal - totalObj;
  const totalPerc = totalObj > 0 ? (totalReal / totalObj) * 100 : null;

  return (
    <div>
      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>
        Individuais — detalhe por vendedor
      </div>

      <div style={{ maxHeight: 260, overflow: "auto", borderRadius: 6 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thMini}></th>
              <th style={thMini}>Vendedor</th>
              <th style={thMini}>Objetivo</th>
              <th style={thMini}>Real</th>
              <th style={thMini}>Dif.</th>
              <th style={thMini}>%</th>
            </tr>
          </thead>

          <tbody>
            {calc.map((r) => (
              <tr key={r.key} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={tdMini}>
                  <IconButton size="small" color="info" onClick={() => onInfoVendedor?.(r)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </td>
                <td style={tdMini}>{r.vendedor}</td>
                <td style={tdMini}>{fmt(r.objetivo)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(r.real)}</td>
                <td style={{ ...tdMini, color: r.dif >= 0 ? "#4caf50" : "#d32f2f" }}>
                  {r.dif.toFixed(2)}
                </td>
                <td style={tdMini}>{r.perc != null ? `${r.perc.toFixed(1)}%` : ""}</td>
              </tr>
            ))}

            {calc.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...tdMini, textAlign: "center", color: "#9e9e9e" }}>
                  Sem dados (nenhum vendedor com objetivo definido).
                </td>
              </tr>
            )}
          </tbody>

          {calc.length > 0 && (
            <tfoot>
              <tr>
                <td style={tdMini}></td>
                <td style={{ ...tdMini, fontWeight: 700 }}>Total</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalObj)}</td>
                <td style={{ ...tdMini, fontWeight: 700 }}>{fmt(totalReal)}</td>
                <td
                  style={{
                    ...tdMini,
                    fontWeight: 700,
                    color: totalDif >= 0 ? "#4caf50" : "#d32f2f",
                  }}
                >
                  {totalDif.toFixed(2)}
                </td>
                <td style={{ ...tdMini, fontWeight: 700 }}>
                  {totalPerc != null ? `${totalPerc.toFixed(1)}%` : ""}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function numFmt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function difCellMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return <span>-</span>;

  const dif = r - o;
  const color = dif > 0 ? "#4caf50" : dif < 0 ? "#d32f2f" : undefined;

  return <span style={{ color, fontWeight: 500 }}>{numFmt(dif)}</span>;
}

function pctFmtMini(objetivo, real) {
  const o = safeNum(objetivo);
  const r = safeNum(real);
  if (!o) return "-";

  const pct = (r / o) * 100;
  return `${pct.toLocaleString("pt-PT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;
}

const thMini = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#fff",
  textAlign: "left",
  padding: "6px 8px",
  fontSize: 12,
  color: "#7b809a",
  verticalAlign: "bottom",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
};

const tdMini = {
  padding: "6px 8px",
  whiteSpace: "nowrap",
};

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function ObjetivosEstadoDashboard({
  autoLoad = true,
  refreshToken = 0,
  onLoadingChange,
  onLoadedChange,
}) {
  const { userData, temFuncao } = useAuth();

  // ⚠️ Ajusta aqui para bater com o teu "isAdminLike" do Objetivos.js
  const isAdminLike =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Gestão Objetivos");

  const refUser = userData?.refvendedor ? String(userData.refvendedor) : "";
  const isVendedor = !!refUser;

  // isAdminLike aqui é só gate (poder ver o componente)
  const canSee = isAdminLike || isVendedor;

  const isComercialRestrito = !isAdminLike && isVendedor;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [objetivosAtivos, setObjetivosAtivos] = useState([]);

  const [carregado, setCarregado] = useState(false);
  const [erro, setErro] = useState("");

  const [temObjetivosEmAtualizacao, setTemObjetivosEmAtualizacao] = useState(false);
  const pollingEmCursoRef = useRef(false);

  const [openInfo, setOpenInfo] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoRow, setInfoRow] = useState(null);
  const [infoDetalhes, setInfoDetalhes] = useState(null);
  const [infoEqRows, setInfoEqRows] = useState([]); // opcional: só usado para Equipa

  const [openVendas, setOpenVendas] = useState(false);
  const [vendasLoading, setVendasLoading] = useState(false);
  const [vendasRow, setVendasRow] = useState(null);
  const [vendasData, setVendasData] = useState([]);

  const vendasColumns = useMemo(() => {
    const cols = [];

    if (vendasRow?.tipo === "equipa") {
      cols.push({
        Header: "Vendedor",
        accessor: "vendedor",
        width: 180,
      });
    }

    cols.push(
      {
        Header: "Cliente",
        accessor: "cliente",
        width: 260,
      },
      {
        Header: "Submarca",
        accessor: "submarca",
        width: 180,
      },
      {
        Header: "Data",
        accessor: "faturadata",
        width: 110,
      },
      {
        Header: "Fatura",
        accessor: "faturanumero",
        width: 130,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 320,
      },
      {
        Header: "QTD",
        accessor: "qtd",
        width: 100,
        sortType: "numPt",
        Cell: ({ value }) => numFmt(value),
      },
      {
        Header: "Euros",
        accessor: "euros",
        width: 110,
        sortType: "numPt",
        Cell: ({ value }) => numFmt(value),
      },
      {
        Header: "Litros",
        accessor: "litros",
        width: 110,
        sortType: "numPt",
        Cell: ({ value }) => numFmt(value),
      }
    );

    return cols;
  }, [vendasRow?.tipo]);

  const vendasDefaultSortBy = useMemo(() => {
    const temSubmarcas =
      Array.isArray(infoDetalhes?.submarcas_selecionadas) &&
      infoDetalhes.submarcas_selecionadas.length > 0;

    if (temSubmarcas) {
      return [
        { id: "cliente", desc: false },
        { id: "submarca", desc: false },
        { id: "faturadata", desc: false },
        { id: "faturanumero", desc: false },
      ];
    }

    return [
      { id: "faturadata", desc: true },
      { id: "faturanumero", desc: false },
    ];
  }, [infoDetalhes]);

  const abrirInfo = async (row) => {
    if (!row?.idobjetivo) return;

    setInfoRow(row);
    setInfoDetalhes(null);
    setInfoEqRows([]);
    setOpenInfo(true);

    setInfoLoading(true);
    try {
      const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: row.idobjetivo });
      const det = detRes.data?.data ?? detRes.data;

      console.log("SelObjetivoDetalhesCache", det);

      setInfoDetalhes(det);
    } catch {
      setInfoDetalhes(null);
    } finally {
      setInfoLoading(false);
    }
  };

  const abrirVendasVendedor = async (row) => {
    const idObjetivo = Number(infoRow?.idobjetivo || 0);
    const refVendedor = String(row?.refvendedor || "").trim();

    if (idObjetivo <= 0 || !refVendedor) return;

    setVendasRow(row);
    setVendasData([]);
    setOpenVendas(true);
    setVendasLoading(true);

    try {
      const res = await api.post("Comercial/SelObjetivoVendasCache", {
        id: idObjetivo,
        idutilizador: Number(userData?.id || 0),
      });

      const arr = Array.isArray(res.data) ? res.data : res.data?.data || [];

      let filtradas = arr.filter((x) => String(x?.refvendedor || "").trim() === refVendedor);

      if (
        infoDetalhes?.clientes_modo === "TODAS_SUBMARCAS" &&
        Array.isArray(infoDetalhes?.submarcas_selecionadas) &&
        infoDetalhes.submarcas_selecionadas.length > 0
      ) {
        filtradas = filtrarClientesTodasSubmarcas(filtradas, infoDetalhes.submarcas_selecionadas);
      }

      setVendasData(filtradas);
    } catch (err) {
      console.log("Falha SelObjetivoVendasCache", err);
      setVendasData([]);
    } finally {
      setVendasLoading(false);
    }
  };

  const abrirVendasEquipa = async () => {
    const idObjetivo = Number(infoRow?.idobjetivo || 0);

    if (idObjetivo <= 0) return;

    setVendasRow({
      vendedor: "Equipa",
      tipo: "equipa",
    });

    setVendasData([]);
    setInfoEqRows([]);
    setOpenVendas(true);
    setVendasLoading(true);

    try {
      const [res, resResumo] = await Promise.all([
        api.post("Comercial/SelObjetivoVendasCache", {
          id: idObjetivo,
          idutilizador: Number(userData?.id || 0),
        }),

        api.post("Comercial/SelObjetivoEquipaResumoVendedores", {
          id: idObjetivo,
        }),
      ]);

      let arr = Array.isArray(res.data) ? res.data : res.data?.data || [];

      const resumoRows = Array.isArray(resResumo.data)
        ? resResumo.data
        : resResumo.data?.data || [];

      setInfoEqRows(
        resumoRows
          .map((linha) => ({
            refvendedor: String(linha?.refvendedor || "").trim(),
            vendedor: linha?.vendedor || linha?.refvendedor || "",
            qtd: safeNum(linha?.qtd),
            euros: safeNum(linha?.euros),
            litros: safeNum(linha?.litros),
            clientes: safeNum(linha?.clientes),
          }))
          .sort((a, b) => String(a.vendedor || "").localeCompare(String(b.vendedor || ""), "pt-PT"))
      );

      const refsEquipa = new Set(
        (Array.isArray(infoDetalhes?.equipa_vendedores) ? infoDetalhes.equipa_vendedores : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      );

      arr = arr.filter((x) => refsEquipa.has(String(x?.refvendedor || "").trim()));

      if (
        infoRow?.metrica === "clientes" &&
        infoDetalhes?.clientes_modo === "TODAS_SUBMARCAS" &&
        Array.isArray(infoDetalhes?.submarcas_selecionadas) &&
        infoDetalhes.submarcas_selecionadas.length > 0
      ) {
        arr = filtrarClientesTodasSubmarcas(arr, infoDetalhes.submarcas_selecionadas);
      }

      setVendasData(arr);
    } catch (err) {
      console.log("Falha ao carregar detalhe de vendas da equipa", err);
      setVendasData([]);
      setInfoEqRows([]);
    } finally {
      setVendasLoading(false);
    }
  };

  const criarRowsObjetivo = (o, det) => {
    if (!o || !det) return [];

    const out = [];

    const listIndVend = Array.isArray(det?.individuais_vendedores)
      ? det.individuais_vendedores
      : [];

    const nomeObjetivo = o?.objetivo_nome || o?.nome || `Objetivo #${o?.id || ""}`;

    const metricas = ["qtd", "euros", "litros", "clientes"];

    for (const met of metricas) {
      // ============================
      // ADMIN → LINHA AGREGADA
      // ============================
      if (isAdminLike) {
        let totalObj = 0;
        let totalReal = 0;

        const isMediaSubmarcas = met === "clientes" && det?.clientes_modo === "MEDIA_SUBMARCAS";

        if (isMediaSubmarcas) {
          totalObj = safeNum(det?.individuais?.clientes?.objetivo);
          totalReal = safeNum(det?.individuais?.clientes?.vendas);
        } else {
          const dadosFabricante = det?.fabricante?.[met];

          totalObj = safeNum(dadosFabricante?.objetivo);
          totalReal = safeNum(dadosFabricante?.vendas);
        }

        if (totalObj <= 0) continue;

        const dif = totalReal - totalObj;
        const perc = totalObj > 0 ? (totalReal / totalObj) * 100 : 0;

        out.push({
          idobjetivo: o.id,
          idobjetivogrupo: o.idobjetivogrupo ?? null,
          grupo_nome: String(o.grupo_nome || "Sem grupo").trim() || "Sem grupo",
          metrica: met,
          tipo: "Vendedor",
          vendedor: "",
          nome: `${nomeObjetivo} (${metricLabel(met)})`,
          inicio: toISO(o.dataini),
          fim: toISO(o.datafim),
          objetivo: totalObj,
          real: totalReal,
          dif,
          perc,
        });

        continue;
      }

      // ============================
      // UTILIZADOR → SÓ O SEU
      // ============================
      const minhaLinha = listIndVend.find((v) => String(v?.refvendedor) === String(refUser));

      if (!minhaLinha?.[met]) continue;

      const dados = minhaLinha[met];
      const objetivo = safeNum(dados?.objetivo);

      if (objetivo <= 0) continue;

      const real = safeNum(dados?.vendas);
      const dif = real - objetivo;
      const perc = objetivo > 0 ? (real / objetivo) * 100 : 0;

      out.push({
        idobjetivo: o.id,
        idobjetivogrupo: o.idobjetivogrupo ?? null,
        grupo_nome: String(o.grupo_nome || "Sem grupo").trim() || "Sem grupo",
        metrica: met,
        tipo: "Vendedor",
        vendedor: minhaLinha?.vendedor || "",
        nome: `${nomeObjetivo} (${metricLabel(met)})`,
        inicio: toISO(o.dataini),
        fim: toISO(o.datafim),
        objetivo,
        real,
        dif,
        perc,
      });
    }

    return out;
  };

  async function loadFromCache({ silencioso = false } = {}) {
    if (!canSee) {
      onLoadingChange?.(false);
      return;
    }

    if (pollingEmCursoRef.current) return;

    pollingEmCursoRef.current = true;

    if (!silencioso) {
      setLoading(true);
      onLoadingChange?.(true);
    }

    setErro("");

    try {
      // 1) lista objetivos a partir da cache
      const bodyObj = isComercialRestrito
        ? { refvendedor: refUser, apenasVigentes: true }
        : { apenasVigentes: true };

      const resObj = await api.post("Comercial/SelObjetivosCache", bodyObj);
      const ativos = Array.isArray(resObj.data) ? resObj.data : resObj.data?.data || [];

      setObjetivosAtivos(ativos);

      // 2) verificar estado de atualização de cada objetivo
      const idsObjetivos = ativos.map((o) => Number(o?.id || 0)).filter((id) => id > 0);

      let estadosObjetivos = [];

      if (idsObjetivos.length > 0) {
        const resEstado = await api.post("Comercial/SelObjetivosCacheEstadoObjetivos", {
          ids: idsObjetivos,
        });

        estadosObjetivos = Array.isArray(resEstado.data)
          ? resEstado.data
          : resEstado.data?.data || [];
      }

      const existemEmAtualizacao = estadosObjetivos.some(
        (e) => e?.is_updating === true || Number(e?.is_updating) === 1
      );

      setTemObjetivosEmAtualizacao(existemEmAtualizacao);

      if (existemEmAtualizacao) {
        setCarregado(true);
        onLoadedChange?.(true);
        return;
      }

      const estadosMap = new Map(estadosObjetivos.map((e) => [Number(e?.idobjetivo || 0), e]));

      const objetivosConcluidos = ativos.filter((o) => {
        const idObjetivo = Number(o?.id || 0);
        const estado = estadosMap.get(idObjetivo);

        // Sem estado confirmado, não usamos o cache desse objetivo.
        if (!estado) return false;

        const isUpdating = estado?.is_updating === true || Number(estado?.is_updating) === 1;

        if (isUpdating) return false;

        const lastError = String(estado?.last_error || "").trim();

        if (lastError) return false;

        return true;
      });

      // 3) carregar detalhe APENAS dos objetivos cujo cache está concluído
      const detalhesAll = await Promise.all(
        objetivosConcluidos.map(async (o) => {
          try {
            const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", { id: o.id });

            const det = detRes.data?.data ?? detRes.data;

            return { obj: o, det };
          } catch (err) {
            console.log("Falha SelObjetivoDetalhesCache", o?.id, err);

            return { obj: o, det: null };
          }
        })
      );

      // console.log("detalhesAll", detalhesAll);

      const out = [];

      for (const it of detalhesAll) {
        const o = it.obj;
        const det = it.det;

        if (!det) {
          console.log("Objetivo sem detalhe em cache", o);
          continue;
        }

        out.push(...criarRowsObjetivo(o, det));
      }

      // console.log("Objetivos dashboard out", out);
      // console.log("Objetivos dashboard out total", out.length);

      setRows(out);
      setCarregado(true);
      onLoadedChange?.(true);
    } catch (e) {
      setErro("Não foi possível carregar os objetivos.");
    } finally {
      pollingEmCursoRef.current = false;

      if (!silencioso) {
        setLoading(false);
        onLoadingChange?.(false);
      }
    }
  }

  const exportarObjetivosExcel = (grupo) => {
    const linhas = Array.isArray(grupo?.rows) ? grupo.rows : [];

    const dados = linhas.map((r) => ({
      Tipo: r?.tipo || "",
      Nome: r?.nome || "",
      "%": safeNum(r?.perc),
      Objetivo: safeNum(r?.objetivo),
      Real: safeNum(r?.real),
      "Dif.": safeNum(r?.dif),
      Início: r?.inicio || "",
      Fim: r?.fim || "",
    }));

    if (dados.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(dados);

    ws["!cols"] = [
      { wch: 14 }, // Tipo
      { wch: 45 }, // Nome
      { wch: 12 }, // %
      { wch: 14 }, // Objetivo
      { wch: 14 }, // Real
      { wch: 14 }, // Dif.
      { wch: 12 }, // Início
      { wch: 12 }, // Fim
    ];

    const wb = XLSX.utils.book_new();

    const nomeGrupo = String(grupo?.nome || "Objetivos").trim() || "Objetivos";

    // Nome de folha Excel: máximo 31 caracteres e sem caracteres inválidos.
    const nomeFolha = nomeGrupo.replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Objetivos";

    XLSX.utils.book_append_sheet(wb, ws, nomeFolha);

    const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

    const nomeFicheiro = nomeGrupo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") || "Objetivos";

    XLSX.writeFile(wb, `${nomeFicheiro}_${hoje}.xlsx`);
  };

  const nomeFolhaExcel = (nome, fallback = "Folha") => {
    const limpo = String(nome || "")
      .replace(/[\\/?*[\]:]/g, "")
      .trim();

    return (limpo || fallback).slice(0, 31);
  };

  const linhasDetalheExcel = (linhas) => {
    return (Array.isArray(linhas) ? linhas : []).map((x) => ({
      Cliente: x?.cliente || "",
      Submarca: x?.submarca || "",
      Data: x?.faturadata || "",
      Fatura: x?.faturanumero || "",
      Material: x?.material || "",
      QTD: safeNum(x?.qtd),
      Euros: safeNum(x?.euros),
      Litros: safeNum(x?.litros),
    }));
  };

  const centrarColunasExcel = (ws, colunas, totalLinhas, incluirCabecalho = true) => {
    const primeiraLinha = incluirCabecalho ? 1 : 2;

    for (let row = primeiraLinha; row <= totalLinhas + 1; row += 1) {
      colunas.forEach((coluna) => {
        const cell = ws[`${coluna}${row}`];

        if (!cell) return;

        cell.s = {
          ...(cell.s || {}),
          alignment: {
            ...(cell.s?.alignment || {}),
            horizontal: "center",
            vertical: "center",
          },
        };
      });
    }
  };

  const formatarCabecalhoExcel = (ws) => {
    if (!ws?.["!ref"]) return;

    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];

      if (!cell) continue;

      cell.s = {
        ...(cell.s || {}),
        font: {
          ...(cell.s?.font || {}),
          bold: true,
        },
      };
    }
  };

  const exportarObjetivoExcel = async (row) => {
    const idObjetivo = Number(row?.idobjetivo || 0);
    const metrica = String(row?.metrica || "").trim();

    if (idObjetivo <= 0 || !metrica) return;

    setLoading(true);

    try {
      // ============================================================
      // 1) Obter detalhe/resumo do objetivo
      // ============================================================
      const detRes = await api.post("Comercial/SelObjetivoDetalhesCache", {
        id: idObjetivo,
      });

      const detalhes = detRes.data?.data ?? detRes.data;

      if (!detalhes) {
        console.log("Exportar objetivo: detalhe não encontrado.", idObjetivo);
        return;
      }

      // ============================================================
      // 2) Obter vendas uma única vez
      // ============================================================
      const vendasRes = await api.post("Comercial/SelObjetivoVendasCache", {
        id: idObjetivo,
        idutilizador: Number(userData?.id || 0),
      });

      const vendasTodas = Array.isArray(vendasRes.data)
        ? vendasRes.data
        : vendasRes.data?.data || [];

      // ============================================================
      // 3) Criar workbook
      // ============================================================
      const wb = XLSX.utils.book_new();

      // ============================================================
      // 4) Folha RESUMO
      // ============================================================
      const resumo = [];

      const dadosEquipa = detalhes?.equipa?.[metrica];

      if (dadosEquipa) {
        const objetivo = safeNum(dadosEquipa?.objetivo);
        const real = safeNum(dadosEquipa?.vendas);

        resumo.push({
          Tipo: "Equipa",
          Vendedor: "",
          Objetivo: objetivo,
          Real: real,
          "Dif.": real - objetivo,
          "%": objetivo > 0 ? Number(((real / objetivo) * 100).toFixed(1)) : null,
        });
      }

      const vendedores = Array.isArray(detalhes?.individuais_vendedores)
        ? detalhes.individuais_vendedores
        : [];

      vendedores.forEach((v) => {
        const dados = v?.[metrica];
        const objetivo = safeNum(dados?.objetivo);

        // Mesmo critério usado no dialog:
        // só apresentamos vendedores com objetivo definido.
        if (objetivo <= 0) return;

        const real = safeNum(dados?.vendas);

        resumo.push({
          Tipo: "Individual",
          Vendedor: v?.vendedor || v?.refvendedor || "",
          Objetivo: objetivo,
          Real: real,
          "Dif.": real - objetivo,
          "%": objetivo > 0 ? Number(((real / objetivo) * 100).toFixed(1)) : null,
        });
      });

      const dadosTotal = detalhes?.fabricante?.[metrica];

      if (dadosTotal) {
        const objetivo = safeNum(dadosTotal?.objetivo);
        const real = safeNum(dadosTotal?.vendas);

        resumo.push({
          Tipo: "Total Geral",
          Vendedor: "",
          Objetivo: objetivo,
          Real: real,
          "Dif.": real - objetivo,
          "%": objetivo > 0 ? Number(((real / objetivo) * 100).toFixed(1)) : null,
        });
      }

      const wsResumo = XLSX.utils.json_to_sheet(resumo);

      formatarCabecalhoExcel(wsResumo);

      wsResumo["!cols"] = [
        { wch: 16 }, // Tipo
        { wch: 32 }, // Vendedor
        { wch: 14 }, // Objetivo
        { wch: 14 }, // Real
        { wch: 14 }, // Dif.
        { wch: 12 }, // %
      ];

      // Percentagem com 1 casa decimal.
      for (let rowExcel = 2; rowExcel <= resumo.length + 1; rowExcel += 1) {
        const cell = wsResumo[`F${rowExcel}`];

        if (cell && typeof cell.v === "number") {
          cell.z = "0.0";
        }
      }

      // Resumo:
      // Tipo e Vendedor ficam à esquerda.
      // Objetivo, Real, Dif. e % ficam centrados.
      centrarColunasExcel(wsResumo, ["C", "D", "E", "F"], resumo.length);

      XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

      // ============================================================
      // 5) Folha EQUIPA
      // ============================================================
      const refsEquipa = new Set(
        (Array.isArray(detalhes?.equipa_vendedores) ? detalhes.equipa_vendedores : [])
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      );

      let vendasEquipa = vendasTodas.filter((x) =>
        refsEquipa.has(String(x?.refvendedor || "").trim())
      );

      if (
        metrica === "clientes" &&
        detalhes?.clientes_modo === "TODAS_SUBMARCAS" &&
        Array.isArray(detalhes?.submarcas_selecionadas) &&
        detalhes.submarcas_selecionadas.length > 0
      ) {
        vendasEquipa = filtrarClientesTodasSubmarcas(vendasEquipa, detalhes.submarcas_selecionadas);
      }

      const wsEquipa = XLSX.utils.json_to_sheet(linhasDetalheExcel(vendasEquipa), {
        header: ["Cliente", "Submarca", "Data", "Fatura", "Material", "QTD", "Euros", "Litros"],
      });

      formatarCabecalhoExcel(wsEquipa);

      wsEquipa["!cols"] = [
        { wch: 34 }, // Cliente
        { wch: 24 }, // Submarca
        { wch: 12 }, // Data
        { wch: 16 }, // Fatura
        { wch: 48 }, // Material
        { wch: 12 }, // QTD
        { wch: 14 }, // Euros
        { wch: 14 }, // Litros
      ];

      // No detalhe, apenas as colunas numéricas do fim ficam centradas.
      centrarColunasExcel(wsEquipa, ["F", "G", "H"], vendasEquipa.length);

      XLSX.utils.book_append_sheet(wb, wsEquipa, "Equipa");

      // ============================================================
      // 6) Uma folha por vendedor individual
      // ============================================================
      const nomesFolhasUsados = new Set(["RESUMO", "EQUIPA"]);

      vendedores.forEach((v, index) => {
        const dados = v?.[metrica];
        const objetivo = safeNum(dados?.objetivo);

        if (objetivo <= 0) return;

        const refVendedor = String(v?.refvendedor || "").trim();

        if (!refVendedor) return;

        let vendasVendedor = vendasTodas.filter(
          (x) => String(x?.refvendedor || "").trim() === refVendedor
        );

        // Mantém exatamente o comportamento atual do dialog individual.
        if (
          detalhes?.clientes_modo === "TODAS_SUBMARCAS" &&
          Array.isArray(detalhes?.submarcas_selecionadas) &&
          detalhes.submarcas_selecionadas.length > 0
        ) {
          vendasVendedor = filtrarClientesTodasSubmarcas(
            vendasVendedor,
            detalhes.submarcas_selecionadas
          );
        }

        let nomeFolha = nomeFolhaExcel(v?.vendedor || refVendedor, `Vendedor ${index + 1}`);

        // Evita colisões entre nomes de folhas.
        const nomeBase = nomeFolha;
        let contador = 2;

        while (nomesFolhasUsados.has(nomeFolha.toUpperCase())) {
          const sufixo = ` ${contador}`;
          nomeFolha = `${nomeBase.slice(0, 31 - sufixo.length)}${sufixo}`;
          contador += 1;
        }

        nomesFolhasUsados.add(nomeFolha.toUpperCase());

        const wsVendedor = XLSX.utils.json_to_sheet(linhasDetalheExcel(vendasVendedor), {
          header: ["Cliente", "Submarca", "Data", "Fatura", "Material", "QTD", "Euros", "Litros"],
        });

        formatarCabecalhoExcel(wsVendedor);

        wsVendedor["!cols"] = [
          { wch: 34 }, // Cliente
          { wch: 24 }, // Submarca
          { wch: 12 }, // Data
          { wch: 16 }, // Fatura
          { wch: 48 }, // Material
          { wch: 12 }, // QTD
          { wch: 14 }, // Euros
          { wch: 14 }, // Litros
        ];

        // No detalhe, apenas QTD, Euros e Litros ficam centrados.
        centrarColunasExcel(wsVendedor, ["F", "G", "H"], vendasVendedor.length);

        XLSX.utils.book_append_sheet(wb, wsVendedor, nomeFolha);
      });

      // ============================================================
      // 7) Nome final do ficheiro
      // ============================================================
      const nomeObjetivo =
        String(row?.nome || `Objetivo_${idObjetivo}`)
          .replace(/\s*\((qtd|euros|litros|clientes)\)\s*$/i, "")
          .trim() || `Objetivo_${idObjetivo}`;

      const nomeFicheiro =
        nomeObjetivo.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_") || `Objetivo_${idObjetivo}`;

      const hoje = new Date().toISOString().slice(0, 10).replaceAll("-", "");

      XLSX.writeFile(wb, `${nomeFicheiro}_${metricLabel(metrica)}_${hoje}.xlsx`);
    } catch (err) {
      console.log("Falha ao exportar objetivo para Excel", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoLoad && refreshToken <= 0) return;
    if (!canSee) return;

    loadFromCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, refreshToken, canSee, userData?.id, userData?.refvendedor]);

  useEffect(() => {
    if (!temObjetivosEmAtualizacao) return undefined;
    if (!canSee) return undefined;

    const intervalId = window.setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (pollingEmCursoRef.current) return;

      pollingEmCursoRef.current = true;

      try {
        const idsObjetivos = objetivosAtivos.map((o) => Number(o?.id || 0)).filter((id) => id > 0);

        if (idsObjetivos.length === 0) {
          setTemObjetivosEmAtualizacao(false);
          return;
        }

        const resEstado = await api.post("Comercial/SelObjetivosCacheEstadoObjetivos", {
          ids: idsObjetivos,
        });

        const estadosObjetivos = Array.isArray(resEstado.data)
          ? resEstado.data
          : resEstado.data?.data || [];

        const aindaEmAtualizacao = estadosObjetivos.some(
          (e) => e?.is_updating === true || Number(e?.is_updating) === 1
        );

        if (aindaEmAtualizacao) {
          setTemObjetivosEmAtualizacao(true);
          return;
        }

        // Terminou o processamento global.
        // Libertamos o controlo antes do carregamento final,
        // porque loadFromCache também usa pollingEmCursoRef.
        pollingEmCursoRef.current = false;

        await loadFromCache({ silencioso: true });
      } catch (err) {
        console.log("Falha polling estados objetivos", err);
      } finally {
        pollingEmCursoRef.current = false;
      }
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temObjetivosEmAtualizacao, canSee, objetivosAtivos]);

  const columns = useMemo(() => {
    const cols = [
      { Header: "Tipo", accessor: "tipo" },
      { Header: "Nome", accessor: "nome" },

      {
        Header: "%",
        accessor: "perc",
        sortType: "numPt",
        Cell: ({ value }) => {
          const valor = Number(value);

          if (!Number.isFinite(valor)) return "";

          const percentagemVisual = Math.max(0, Math.min(100, valor));

          return (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 150,
              }}
            >
              <MDTypography
                variant="caption"
                sx={{
                  minWidth: 42,
                  textAlign: "right",
                  fontSize: 12,
                  color: "#344767",
                }}
              >
                {valor.toFixed(1)}%
              </MDTypography>

              <MDBox
                sx={{
                  position: "relative",
                  width: 92,
                  height: 9,
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.10)",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: `${percentagemVisual}%`,
                    height: "100%",
                    overflow: "hidden",
                    transition: "width 0.25s ease",
                  }}
                >
                  <MDBox
                    sx={{
                      width: 92,
                      height: "100%",
                      background:
                        "linear-gradient(90deg, #f44336 0%, #ff5722 25%, #ffeb3b 50%, #8bc34a 75%, #4caf50 100%)",
                    }}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          );
        },
      },

      {
        Header: "Objetivo",
        accessor: "objetivo",
        sortType: "numPt",
        Cell: ({ value }) =>
          safeNum(value).toLocaleString("pt-PT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
      },
      {
        Header: !isAdminLike ? "Real (meu)" : "Real",
        accessor: "real",
        sortType: "numPt",
        Cell: ({ value }) => (
          <b>
            {safeNum(value).toLocaleString("pt-PT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </b>
        ),
      },
      {
        Header: "Dif.",
        accessor: "dif",
        sortType: "numPt",
        Cell: ({ value }) => {
          const v = safeNum(value);
          const txt = Number.isFinite(v) ? v.toFixed(2) : "";
          return <span style={{ color: v >= 0 ? "#4caf50" : "#d32f2f" }}>{txt}</span>;
        },
      },

      { Header: "Início", accessor: "inicio" },
      { Header: "Fim", accessor: "fim" },
    ];

    if (!isAdminLike) {
      cols.splice(1, 0, {
        Header: "Vendedor",
        accessor: "vendedor",
      });
    }

    return cols;
  }, [isAdminLike]);

  const rowActions = useMemo(() => {
    if (!isAdminLike) return [];

    return [
      {
        id: "info",
        icon: <InfoOutlinedIcon fontSize="small" />,
        tooltip: "Ver detalhe por vendedor",
        color: "info",
        onClick: (row) => abrirInfo(row),
      },
      {
        id: "excel",
        icon: <TableViewIcon fontSize="small" />,
        tooltip: "Exportar objetivo para Excel",
        color: "success",
        onClick: (row) => exportarObjetivoExcel(row),
      },
    ];
  }, [isAdminLike]);

  const grupos = useMemo(() => {
    const map = new Map();

    rows.forEach((r) => {
      const id = r.idobjetivogrupo ?? 0;
      const nome = String(r.grupo_nome || "Sem grupo").trim() || "Sem grupo";

      if (!map.has(id)) {
        map.set(id, {
          id,
          nome,
          rows: [],
          idsObjetivos: new Set(),
        });
      }

      const grupo = map.get(id);

      grupo.rows.push(r);

      if (r.idobjetivo != null) {
        grupo.idsObjetivos.add(String(r.idobjetivo));
      }
    });

    return Array.from(map.values())
      .map((grupo) => ({
        id: grupo.id,
        nome: grupo.nome,
        rows: grupo.rows,
        totalObjetivos: grupo.idsObjetivos.size,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-PT"));
  }, [rows]);

  if (!canSee) return null;

  return (
    <>
      <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
        {loading && carregado ? (
          <MDBox py={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </MDBox>
        ) : null}

        {carregado && temObjetivosEmAtualizacao && (
          <MDBox
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              border: "1px solid rgba(255, 152, 0, 0.35)",
              backgroundColor: "rgba(255, 152, 0, 0.06)",
            }}
          >
            <MDTypography
              variant="button"
              sx={{
                display: "block",
                color: "#b26a00",
              }}
            >
              Objetivos em atualização. Os resultados serão apresentados quando o recálculo
              terminar.
            </MDTypography>
          </MDBox>
        )}

        {carregado && !temObjetivosEmAtualizacao && grupos.length > 0 && (
          <MDBox
            sx={{
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {grupos.map((grupo) => {
              const textoQuantidade =
                grupo.totalObjetivos === 1 ? "1 objetivo" : `${grupo.totalObjetivos} objetivos`;

              return (
                <DashboardLazyAccordion
                  key={`objetivos-grupo-${grupo.id}`}
                  title={`${grupo.nome} (${textoQuantidade})`}
                  headerContent={
                    <Tooltip title="Exportar este grupo para Excel" arrow placement="top">
                      <IconButton
                        size="small"
                        color="success"
                        aria-label={`Exportar ${grupo.nome} para Excel`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          exportarObjetivosExcel(grupo);
                        }}
                      >
                        <TableViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  }
                  loaded
                  showRefreshButton={false}
                >
                  {grupo.rows.length > 0 && (
                    <ProjectTable
                      tableKey={`dashboard-objetivos-${grupo.id}`}
                      version="v1"
                      columns={columns}
                      data={grupo.rows}
                      rowActions={rowActions}
                      actionsPosition="left"
                      emptyMessage="Sem objetivos para apresentar."
                      minWidth={900}
                      maxHeight={360}
                      showKeepFilters
                    />
                  )}
                </DashboardLazyAccordion>
              );
            })}
          </MDBox>
        )}

        {carregado && !temObjetivosEmAtualizacao && grupos.length === 0 && !loading && (
          <MDBox py={2}>
            <MDTypography
              variant="button"
              color="text"
              sx={{
                display: "block",
                textAlign: "center",
                fontWeight: "regular",
              }}
            >
              Não foram encontrados objetivos para apresentar.
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <Dialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        maxWidth="md"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          <MDTypography variant="h5" fontWeight="bold">
            {infoRow?.nome
              ? String(infoRow.nome).replace(/\s*\((qtd|euros|litros|clientes)\)\s*$/i, "")
              : "Objetivo"}
          </MDTypography>

          <MDTypography variant="button" color="text" fontWeight="regular">
            Detalhe por vendedor
          </MDTypography>
        </DialogTitle>

        <DialogContent dividers>
          {infoLoading && (
            <MDBox display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={20} />
              <span>A carregar…</span>
            </MDBox>
          )}

          {!infoLoading && !infoDetalhes && <div style={{ opacity: 0.8 }}>Sem dados.</div>}

          {!infoLoading && infoDetalhes && (
            <>
              {Array.isArray(infoDetalhes?.submarcas_selecionadas) &&
                infoDetalhes.submarcas_selecionadas.length > 0 && (
                  <MDBox mb={2}>
                    <MDTypography
                      variant="button"
                      sx={{
                        display: "block",
                        fontSize: 12,
                        color: "#7b809a",
                        mb: 1,
                      }}
                    >
                      Submarcas selecionadas
                    </MDTypography>

                    <MDBox
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {infoDetalhes.submarcas_selecionadas.map((sm) => (
                        <MDBox
                          key={sm.ref}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            backgroundColor: "#fafafa",
                          }}
                        >
                          <MDTypography
                            variant="button"
                            sx={{
                              display: "block",
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          >
                            {sm.nome}
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  </MDBox>
                )}

              <MDBox mb={2}>
                <MDTypography
                  variant="button"
                  sx={{
                    display: "block",
                    fontSize: 12,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Equipa
                </MDTypography>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                    marginBottom: 16,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thMini}></th>
                      <th style={thMini}>Equipa</th>
                      <th style={thMini}>Objetivo</th>
                      <th style={thMini}>Real</th>
                      <th style={thMini}>Dif.</th>
                      <th style={thMini}>%</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(() => {
                      const met = infoRow?.metrica;
                      const dados = infoDetalhes?.equipa?.[met];

                      const objetivo = safeNum(dados?.objetivo);
                      const real = safeNum(dados?.vendas);
                      const dif = real - objetivo;
                      const perc = objetivo > 0 ? (real / objetivo) * 100 : null;

                      return (
                        <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={tdMini}>
                            <Tooltip title="Ver detalhe de vendas da equipa" arrow placement="top">
                              <IconButton size="small" color="info" onClick={abrirVendasEquipa}>
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </td>

                          <td style={tdMini}>{infoDetalhes?.equipa?.nome || "Equipa"}</td>

                          <td style={tdMini}>{numFmt(objetivo)}</td>

                          <td style={{ ...tdMini, fontWeight: 700 }}>{numFmt(real)}</td>

                          <td
                            style={{
                              ...tdMini,
                              color: dif >= 0 ? "#4caf50" : "#d32f2f",
                            }}
                          >
                            {numFmt(dif)}
                          </td>

                          <td style={tdMini}>{perc != null ? `${perc.toFixed(1)}%` : "-"}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </MDBox>

              <VendedoresMiniTable
                vendedores={infoDetalhes?.individuais_vendedores}
                tipoKey={infoRow?.metrica}
                onInfoVendedor={abrirVendasVendedor}
              />

              <MDBox mt={2}>
                <MDTypography
                  variant="button"
                  sx={{
                    display: "block",
                    fontSize: 12,
                    color: "#7b809a",
                    mb: 1,
                  }}
                >
                  Total Geral
                </MDTypography>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thMini}>Tipo</th>
                      <th style={thMini}>Objetivo</th>
                      <th style={thMini}>Real</th>
                      <th style={thMini}>Dif.</th>
                      <th style={thMini}>%</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(() => {
                      const met = infoRow?.metrica;
                      const dados = infoDetalhes?.fabricante?.[met];

                      const objetivo = safeNum(dados?.objetivo);
                      const real = safeNum(dados?.vendas);
                      const dif = real - objetivo;
                      const perc = objetivo > 0 ? (real / objetivo) * 100 : null;

                      return (
                        <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ ...tdMini, fontWeight: 700 }}>Total Geral</td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>{numFmt(objetivo)}</td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>{numFmt(real)}</td>
                          <td
                            style={{
                              ...tdMini,
                              fontWeight: 700,
                              color: dif >= 0 ? "#4caf50" : "#d32f2f",
                            }}
                          >
                            {numFmt(dif)}
                          </td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>
                            {perc != null ? `${perc.toFixed(1)}%` : "-"}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </MDBox>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenInfo(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openVendas}
        onClose={() => setOpenVendas(false)}
        maxWidth="xl"
        fullWidth
        PaperComponent={PaperComponent}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
          Detalhe de vendas — {vendasRow?.vendedor || ""}
        </DialogTitle>

        <DialogContent dividers>
          {vendasLoading ? (
            <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} py={3}>
              <CircularProgress size={22} />
              <MDTypography variant="button">A carregar vendas...</MDTypography>
            </MDBox>
          ) : (
            <>
              {vendasRow?.tipo === "equipa" && infoEqRows.length > 0 && (
                <MDBox mb={2}>
                  <MDTypography
                    variant="button"
                    sx={{
                      display: "block",
                      fontSize: 12,
                      color: "#7b809a",
                      mb: 1,
                    }}
                  >
                    Resumo por vendedor
                  </MDTypography>

                  <div
                    style={{
                      maxHeight: 260,
                      overflow: "auto",
                      borderRadius: 6,
                      marginBottom: 8,
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={thMini}>Vendedor</th>
                          <th style={thMini}>QTD</th>
                          <th style={thMini}>Euros</th>
                          <th style={thMini}>Litros</th>
                          <th style={thMini}>Clientes</th>
                        </tr>
                      </thead>

                      <tbody>
                        {infoEqRows.map((r) => (
                          <tr
                            key={r.refvendedor || r.vendedor}
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <td style={tdMini}>{r.vendedor}</td>
                            <td style={tdMini}>{numFmt(r.qtd)}</td>
                            <td style={tdMini}>{numFmt(r.euros)}</td>
                            <td style={tdMini}>{numFmt(r.litros)}</td>
                            <td style={tdMini}>{numFmt(r.clientes)}</td>
                          </tr>
                        ))}
                      </tbody>

                      <tfoot>
                        <tr>
                          <td style={{ ...tdMini, fontWeight: 700 }}>Total Equipa</td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>
                            {numFmt(infoEqRows.reduce((a, r) => a + safeNum(r.qtd), 0))}
                          </td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>
                            {numFmt(infoEqRows.reduce((a, r) => a + safeNum(r.euros), 0))}
                          </td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>
                            {numFmt(infoEqRows.reduce((a, r) => a + safeNum(r.litros), 0))}
                          </td>
                          <td style={{ ...tdMini, fontWeight: 700 }}>
                            {numFmt(infoEqRows.reduce((a, r) => a + safeNum(r.clientes), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </MDBox>
              )}

              <ProjectTable
                columns={vendasColumns}
                data={vendasData}
                defaultSortBy={vendasDefaultSortBy}
                emptyMessage="Sem vendas para apresentar."
                maxHeight={420}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenVendas(false)}>
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
